import type { Value } from "platejs";
import { Plate, usePlateEditor } from "platejs/react";
import { useCallback, useMemo, useRef } from "react";
import { Editor, EditorContainer } from "./editor";
import { WikiEditorKit } from "./plugins/wiki-editor-kit";

interface WikiEditorProps {
	initialContent?: unknown;
	onChange?: (content: unknown) => void;
	readOnly?: boolean;
}

const DEFAULT_CONTENT: Value = [{ type: "p", children: [{ text: "" }] }];

/**
 * Converts legacy content formats to Plate.js Value.
 * Handles: Plate arrays, TipTap doc objects, plain text objects.
 */
function normalizeContent(content: unknown): Value {
	// Already Plate.js format: [{ type: "p", children: [...] }, ...]
	if (Array.isArray(content) && content.length > 0) {
		return content as Value;
	}

	if (content && typeof content === "object" && "type" in content) {
		const obj = content as Record<string, unknown>;

		// TipTap format: { type: "doc", content: [...] }
		if (obj.type === "doc" && Array.isArray(obj.content)) {
			return convertTiptapNodes(obj.content as TiptapNode[]);
		}

		// Old plain text format: { type: "text", text: "..." }
		if (obj.type === "text" && "text" in obj) {
			const text = (obj.text as string) || "";
			if (text) {
				return text.split("\n").map((line) => ({
					type: "p" as const,
					children: [{ text: line }],
				}));
			}
		}
	}

	return DEFAULT_CONTENT;
}

// --- TipTap → Plate conversion ---

interface TiptapNode {
	type?: string;
	text?: string;
	attrs?: Record<string, unknown>;
	content?: TiptapNode[];
	marks?: { type: string; attrs?: Record<string, unknown> }[];
	[key: string]: unknown;
}

const TIPTAP_TYPE_MAP: Record<string, string> = {
	paragraph: "p",
	heading: "h", // level appended dynamically
	blockquote: "blockquote",
	codeBlock: "code_block",
	code_block: "code_block",
	bulletList: "ul",
	bullet_list: "ul",
	orderedList: "ol",
	ordered_list: "ol",
	listItem: "li",
	list_item: "li",
	taskList: "action_item",
	taskItem: "action_item",
	horizontalRule: "hr",
	horizontal_rule: "hr",
	table: "table",
	tableRow: "table_row",
	table_row: "table_row",
	tableCell: "td",
	table_cell: "td",
	tableHeader: "th",
	table_header: "th",
	image: "img",
	hardBreak: "p",
	hard_break: "p",
};

const TIPTAP_MARK_MAP: Record<string, string> = {
	bold: "bold",
	italic: "italic",
	underline: "underline",
	strike: "strikethrough",
	code: "code",
	highlight: "highlight",
	subscript: "subscript",
	superscript: "superscript",
};

function convertTiptapNodes(nodes: TiptapNode[]): Value {
	const result = [] as unknown as Value;
	for (const node of nodes) {
		const converted = convertTiptapNode(node);
		if (converted) (result as unknown[]).push(converted);
	}
	return (result as unknown[]).length > 0 ? result : DEFAULT_CONTENT;
}

function convertTiptapNode(node: TiptapNode): Record<string, unknown> | null {
	// Text leaf node
	if (node.type === "text" || (!node.type && node.text != null)) {
		const leaf: Record<string, unknown> = { text: node.text ?? "" };
		if (node.marks) {
			for (const mark of node.marks) {
				const plateKey = TIPTAP_MARK_MAP[mark.type];
				if (plateKey) leaf[plateKey] = true;
			}
		}
		return leaf;
	}

	// Hard break → just a newline text
	if (node.type === "hardBreak" || node.type === "hard_break") {
		return { text: "\n" };
	}

	// Element node
	let plateType = TIPTAP_TYPE_MAP[node.type ?? ""] ?? node.type ?? "p";

	// Heading: append level
	if (node.type === "heading" && node.attrs?.level) {
		plateType = `h${node.attrs.level}`;
	}

	const element: Record<string, unknown> = { type: plateType };

	// Copy relevant attrs
	if (node.type === "image" && node.attrs?.src) {
		element.url = node.attrs.src;
	}
	if (node.type === "codeBlock" || node.type === "code_block") {
		if (node.attrs?.language) element.lang = node.attrs.language;
	}

	// Convert children
	const children: Record<string, unknown>[] = [];
	if (node.content && node.content.length > 0) {
		for (const child of node.content) {
			const converted = convertTiptapNode(child);
			if (converted) children.push(converted);
		}
	}

	element.children = children.length > 0 ? children : [{ text: "" }];
	return element;
}

export function WikiEditor({ initialContent, onChange, readOnly = false }: WikiEditorProps) {
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const value = useMemo(() => normalizeContent(initialContent), [initialContent]);

	const editor = usePlateEditor({
		plugins: WikiEditorKit,
		value,
	});

	const handleChange = useCallback(
		({ value: newValue }: { value: Value }) => {
			if (!onChange) return;

			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}
			debounceRef.current = setTimeout(() => {
				onChange(newValue);
			}, 2000);
		},
		[onChange],
	);

	return (
		<Plate editor={editor} onValueChange={!readOnly ? handleChange : undefined} readOnly={readOnly}>
			<EditorContainer>
				<Editor
					placeholder={!readOnly ? "Press '/' for commands..." : undefined}
					readOnly={readOnly}
				/>
			</EditorContainer>
		</Plate>
	);
}
